import { Column } from './ColumnsStore'

interface ReconciliationResult {
  reconciledColumns: Column[]
  reconciledKnownIds: string[]
  hasChanges: boolean
}

const isAsyncCampaignColumnId = (columnId: string) =>
  columnId.startsWith('ai_column_')

/**
 * Reconciles stored column preferences with current table columns.
 *
 * This runs once during table initialization to sync localStorage with
 * the current column definitions. It handles:
 * - Removing stale columns (no longer exist in table)
 * - Adding new columns (based on defaultColumnVisibility)
 * - Updating knownColumnIds to include new columns
 *
 * @param storedColumns - User's saved visible columns from localStorage
 * @param storedKnownIds - All column IDs that existed when user last saved
 * @param allCurrentColumns - All columns currently in the table
 * @param defaultColumnVisibility - Default visibility settings for columns
 * @returns Reconciled columns, known IDs, and whether changes were made
 */
export function reconcileStoredColumns(
  storedColumns: Column[] | undefined,
  storedKnownIds: string[] | undefined,
  allCurrentColumns: Column[],
  defaultColumnVisibility: Record<string, boolean> | undefined
): ReconciliationResult {
  // No known columns = first-time user, nothing to reconcile
  if (!storedKnownIds) {
    return {
      reconciledColumns: storedColumns ?? [],
      reconciledKnownIds: [],
      hasChanges: false,
    }
  }

  const knownIds = new Set(storedKnownIds)
  const currentIds = new Set(allCurrentColumns.map((c) => c.id))
  const currentColumnsById = new Map(allCurrentColumns.map((c) => [c.id, c]))

  // Find new columns (in current but not in known)
  const newColumnIds = [...currentIds].filter((id) => !knownIds.has(id))

  // Find stale columns (in known but not in current)
  const staleColumnIds = [...knownIds].filter(
    (id) => !currentIds.has(id) && !isAsyncCampaignColumnId(id)
  )

  const metadataChanged = (storedColumns ?? []).some((storedColumn) => {
    const currentColumn = currentColumnsById.get(storedColumn.id)
    return (
      currentColumn &&
      (currentColumn.title !== storedColumn.title ||
        currentColumn.isEditable !== storedColumn.isEditable ||
        currentColumn.isHidden !== storedColumn.isHidden ||
        currentColumn.isFirstColumn !== storedColumn.isFirstColumn ||
        currentColumn.isLastColumn !== storedColumn.isLastColumn)
    )
  })

  // No changes needed
  if (
    newColumnIds.length === 0 &&
    staleColumnIds.length === 0 &&
    !metadataChanged
  ) {
    return {
      reconciledColumns: storedColumns ?? [],
      reconciledKnownIds: storedKnownIds,
      hasChanges: false,
    }
  }

  // Build reconciledColumns preserving user's custom ordering
  // Start with user's order, filter out stale columns, then insert new columns at canonical positions
  const newIds = new Set(newColumnIds)

  // Keep user's order for existing columns, filtering out stale ones and
  // refreshing titles/groups from current definitions.
  const reconciledColumns = (storedColumns ?? [])
    .map((c) => {
      const currentColumn = currentColumnsById.get(c.id)
      if (currentColumn) return currentColumn
      return isAsyncCampaignColumnId(c.id) ? c : undefined
    })
    .filter((c): c is Column => !!c)

  // Insert new columns at their canonical positions
  for (const column of allCurrentColumns) {
    if (newIds.has(column.id)) {
      const shouldBeVisible = defaultColumnVisibility?.[column.id] !== false
      if (shouldBeVisible && !column.isHidden) {
        // Find insertion point based on canonical order
        const canonicalIndex = allCurrentColumns.indexOf(column)

        // Find the closest existing column before this one in canonical order
        let insertIndex = 0
        for (let i = canonicalIndex - 1; i >= 0; i--) {
          const beforeColumn = allCurrentColumns[i]
          const beforeIndex = reconciledColumns.findIndex(
            (c) => c.id === beforeColumn.id
          )
          if (beforeIndex !== -1) {
            insertIndex = beforeIndex + 1
            break
          }
        }

        reconciledColumns.splice(insertIndex, 0, column)
      }
    }
  }

  // Update knownColumnIds: remove stale, add new
  const reconciledKnownIds = storedKnownIds.filter(
    (id) => currentIds.has(id) || isAsyncCampaignColumnId(id)
  )
  reconciledKnownIds.push(...newColumnIds)

  return { reconciledColumns, reconciledKnownIds, hasChanges: true }
}
